import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import {
  FaYoutube,
  FaFacebookSquare,
  FaSearch,
  FaTimes,
  FaSyncAlt,
  FaCheck,
  FaBan,
  FaRupeeSign,
  FaHourglassHalf,
  FaUserPlus,
  FaInbox,
  FaSearchPlus,
  FaSearchMinus,
  FaRedo,
  FaExternalLinkAlt,
  FaCopy,
  FaArrowRight,
  FaUserFriends,
  FaReceipt,
  FaExclamationTriangle,
  FaLayerGroup,
  FaImage,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

/* ================= styles ================= */

const CSS = `
@keyframes jrFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
@keyframes jrFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes jrZoom { from { opacity: 0; transform: scale(.95); } to { opacity: 1; transform: none; } }
@keyframes jrOut { to { opacity: 0; transform: translateX(40px) scale(.97); } }
@keyframes jrStamp { 0% { opacity: 0; transform: scale(1.6) rotate(-12deg); } 60% { opacity: 1; transform: scale(.95) rotate(-12deg); } 100% { transform: scale(1) rotate(-12deg); } }
@keyframes jrPulse { 0% { box-shadow: 0 0 0 0 rgba(245,158,11,.5); } 70% { box-shadow: 0 0 0 7px rgba(245,158,11,0); } 100% { box-shadow: 0 0 0 0 rgba(245,158,11,0); } }
.jr-in { animation: jrFadeUp .5s cubic-bezier(.2,.7,.2,1) both; }
.jr-fade { animation: jrFade .2s ease both; }
.jr-zoom { animation: jrZoom .25s cubic-bezier(.2,.8,.2,1) both; }
.jr-out { animation: jrOut .45s ease forwards; }
.jr-stamp { animation: jrStamp .45s cubic-bezier(.2,.8,.2,1) both; }
.jr-dot { animation: jrPulse 1.8s ease-out infinite; }
.jr-press { transition: transform .15s ease, background-color .2s ease, border-color .2s ease, color .2s ease; }
.jr-press:hover { transform: translateY(-1px); }
.jr-press:active { transform: scale(.96); }
.jr-ticket { transition: transform .25s cubic-bezier(.2,.8,.2,1), box-shadow .25s ease; }
.jr-ticket:hover { transform: translateY(-3px); box-shadow: 0 18px 34px -18px rgba(15,23,42,.4); }
.jr-proof img { transition: transform .4s cubic-bezier(.2,.8,.2,1); }
.jr-proof:hover img { transform: scale(1.05); }
.jr-mark { background: #fde68a; color: inherit; border-radius: 3px; padding: 0 1px; }
.jr-perf { background-image: radial-gradient(circle at 8px 50%, transparent 7px, currentColor 7.5px); background-size: 16px 16px; }
@media (prefers-reduced-motion: reduce) {
  .jr-in, .jr-fade, .jr-zoom, .jr-out, .jr-stamp, .jr-dot { animation: none; }
  .jr-press:hover, .jr-press:active, .jr-ticket:hover, .jr-proof:hover img { transform: none; }
}
`;

const CARD = "bg-white dark:bg-secondary-dark-bg rounded-2xl border border-slate-200 dark:border-slate-700";
const INPUT =
  "rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500";

const REASONS = [
  "Payment not received",
  "Wrong amount paid",
  "Payment proof is not clear",
  "Fake or edited screenshot",
  "Duplicate request",
];

/* ================= helpers ================= */

const DAY = 24 * 60 * 60 * 1000;
const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const parseAny = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDate = (d) => (d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-");
const fmtTime = (d) => (d ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "");
const waiting = (d) => {
  if (!d) return "";
  const h = Math.floor((Date.now() - d.getTime()) / 3600000);
  if (h < 1) return "Just now";
  if (h < 24) return `${h} hr waiting`;
  const days = Math.floor(h / 24);
  return `${days} day${days > 1 ? "s" : ""} waiting`;
};
const hasProof = (url) => url && !/\/$/.test(url) && !/\/(null|0)$/.test(url);
const nm = (s) => (s && String(s).trim() ? s : null);

const Hi = ({ text, q }) => {
  const s = String(text ?? "");
  if (!q || !s) return <>{s}</>;
  const i = s.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{s}</>;
  return (
    <>
      {s.slice(0, i)}
      <mark className="jr-mark">{s.slice(i, i + q.length)}</mark>
      {s.slice(i + q.length)}
    </>
  );
};

const Initials = ({ name, size = 36, tone = "bg-slate-200 text-slate-600 dark:bg-slate-600 dark:text-slate-200" }) => {
  const t =
    String(name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  return (
    <span className={`rounded-full shrink-0 flex items-center justify-center font-semibold ${tone}`} style={{ width: size, height: size, fontSize: size * 0.38 }}>
      {t}
    </span>
  );
};

/* ================= page ================= */

const GroupJoinRequest = () => {
  const [requests, setRequests] = useState([]);
  const [platformMap, setPlatformMap] = useState({});
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");
  const [platform, setPlatform] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [sortBy, setSortBy] = useState("oldest");
  const [checked, setChecked] = useState([]);

  const [viewer, setViewer] = useState(null); // request
  const [zoom, setZoom] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [confirm, setConfirm] = useState(null); // {type, list}
  const [reason, setReason] = useState(REASONS[0]);
  const [customReason, setCustomReason] = useState("");
  const [working, setWorking] = useState(false);
  const [leaving, setLeaving] = useState({}); // join_id -> 'approved' | 'rejected'

  const toast = useRef(null);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const { baseUrl } = useBaseUrl();
  const { currentMode } = useStateContext();

  /* ----- API (same actions) ----- */

  const post = useCallback(
    async (action, extra = {}) => {
      const fd = new FormData();
      fd.append("action", action);
      Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(`${baseUrl}/group.php`, { method: "POST", body: fd, credentials: "include" });
      return res.json();
    },
    [baseUrl]
  );

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const [reqs, groups] = await Promise.all([post("groupJoiningRequestList"), post("groupListWithMembers").catch(() => null)]);
      setRequests(Array.isArray(reqs?.requests) ? reqs.requests : []);
      const map = {};
      (groups?.groups || []).forEach((g) => {
        map[String(g.group_id)] = String(g.platform || "").toLowerCase();
      });
      setPlatformMap(map);
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Could not load joining requests" });
    }
    setLoading(false);
  }, [post]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const platformOf = useCallback(
    (r) => {
      const p = platformMap[String(r.group_id)];
      if (p === "facebook" || p === "youtube") return p;
      if (Number(r.group_id) === 2 || /facebook/i.test(r.grp_name || "")) return "facebook";
      if (Number(r.group_id) === 1 || /youtube/i.test(r.grp_name || "")) return "youtube";
      return "other";
    },
    [platformMap]
  );

  const runConfirm = async () => {
    if (!confirm) return;
    const { type, list } = confirm;
    const finalReason = (reason === "__custom" ? customReason : reason).trim() || "Your request was not approved";
    setWorking(true);
    let ok = 0;
    const errors = [];
    for (const r of list) {
      try {
        const data = await post(type === "approve" ? "approveJoinRequest" : "rejectJoinRequest", type === "approve" ? { join_id: r.join_id } : { join_id: r.join_id, reason: finalReason });
        if (data?.error !== 0) throw new Error(typeof data?.error === "string" ? data.error : data?.message || "Failed");
        ok += 1;
        setLeaving((l) => ({ ...l, [r.join_id]: type === "approve" ? "approved" : "rejected" }));
      } catch (e) {
        errors.push(`${nm(r.user_name) || r.user_id}: ${e.message}`);
      }
    }
    setWorking(false);
    setConfirm(null);
    setViewer(null);
    setChecked([]);
    setCustomReason("");
    if (ok) {
      toast.current?.show({
        severity: "success",
        summary: type === "approve" ? "Approved" : "Rejected",
        detail:
          type === "approve"
            ? `${ok} request${ok > 1 ? "s" : ""} approved. Member added and commission processed.`
            : `${ok} request${ok > 1 ? "s" : ""} rejected. User has been notified.`,
      });
    }
    if (errors.length) {
      toast.current?.show({ severity: "error", summary: "Some requests failed", detail: errors.slice(0, 3).join(" | "), life: 7000 });
    }
    setTimeout(() => {
      setLeaving({});
      fetchRequests();
    }, 700);
  };

  /* ----- derived ----- */

  const q = query.trim();

  const groupsInList = useMemo(() => {
    const m = {};
    requests.forEach((r) => {
      m[r.group_id] = r.grp_name || `Group ${r.group_id}`;
    });
    return Object.entries(m);
  }, [requests]);

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    let l = requests.filter((r) => {
      if (platform !== "all" && platformOf(r) !== platform) return false;
      if (groupFilter !== "all" && String(r.group_id) !== String(groupFilter)) return false;
      if (!ql) return true;
      return [r.user_name, r.user_id, r.joined_by_name, r.joined_by, r.grp_name, r.group_id, r.join_id, r.paid_amount].map((v) => String(v ?? "").toLowerCase()).some((v) => v.includes(ql));
    });
    l = [...l].sort((a, b) => {
      if (sortBy === "amount") return Number(b.paid_amount || 0) - Number(a.paid_amount || 0);
      const da = parseAny(a.date_time)?.getTime() || Number(a.join_id) || 0;
      const db = parseAny(b.date_time)?.getTime() || Number(b.join_id) || 0;
      return sortBy === "oldest" ? da - db : db - da;
    });
    return l;
  }, [requests, q, platform, groupFilter, sortBy, platformOf]);

  useEffect(() => {
    const valid = new Set(requests.map((r) => r.join_id));
    setChecked((c) => c.filter((id) => valid.has(id)));
  }, [requests]);

  const stats = useMemo(() => {
    const oldest = [...requests].sort((a, b) => (parseAny(a.date_time)?.getTime() || 0) - (parseAny(b.date_time)?.getTime() || 0))[0];
    return {
      pending: requests.length,
      amount: requests.reduce((s, r) => s + Number(r.paid_amount || 0), 0),
      referrers: new Set(requests.map((r) => r.joined_by).filter(Boolean)).size,
      old: requests.filter((r) => {
        const d = parseAny(r.date_time);
        return d && Date.now() - d.getTime() > DAY;
      }).length,
      oldestText: oldest ? waiting(parseAny(oldest.date_time)) : "-",
    };
  }, [requests]);

  const checkedList = requests.filter((r) => checked.includes(r.join_id));
  const allChecked = filtered.length > 0 && filtered.every((r) => checked.includes(r.join_id));

  useEffect(() => {
    setZoom(1);
    setRotate(0);
  }, [viewer]);

  useEffect(() => {
    const onKey = (e) => {
      const typing = ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName);
      if (confirm) {
        if (e.key === "Escape" && !working) setConfirm(null);
        return;
      }
      if (viewer) {
        if (e.key === "Escape") setViewer(null);
        if (!typing && (e.key === "a" || e.key === "A")) setConfirm({ type: "approve", list: [viewer] });
        if (!typing && (e.key === "r" || e.key === "R")) setConfirm({ type: "reject", list: [viewer] });
        return;
      }
      if (!typing && e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirm, working, viewer]);

  const copyText = (text, label = "Copied") => {
    navigator.clipboard?.writeText(String(text));
    toast.current?.show({ severity: "info", summary: label, detail: String(text), life: 1500 });
  };

  const PlatformBadge = ({ r }) => {
    const p = platformOf(r);
    if (p === "facebook")
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
          <FaFacebookSquare size={10} /> Facebook
        </span>
      );
    if (p === "youtube")
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
          <FaYoutube size={10} /> YouTube
        </span>
      );
    return <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Group</span>;
  };

  /* ================= render ================= */

  return (
    <div style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}} className="w-full p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen">
      <style>{CSS}</style>
      <Toast ref={toast} />

      {/* Header */}
      <div className="jr-in flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">Group</p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Joining Requests</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Check each payment proof, then accept to add the member or cancel with a reason.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => navigate("/group/list")} className="jr-press inline-flex items-center gap-2 h-9 px-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200">
            <FaLayerGroup size={12} /> Groups
          </button>
          <button type="button" onClick={fetchRequests} disabled={loading} className="jr-press inline-flex items-center gap-2 h-9 px-4 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold disabled:opacity-60">
            <FaSyncAlt size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "Pending requests", value: fmt(stats.pending), icon: FaUserPlus, tint: "bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300" },
          { label: "Money to verify", value: money(stats.amount), icon: FaRupeeSign, tint: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300" },
          { label: "Waiting over 1 day", value: fmt(stats.old), icon: FaHourglassHalf, tint: "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300", sub: stats.pending ? `Oldest: ${stats.oldestText}` : "" },
          { label: "Referrers involved", value: fmt(stats.referrers), icon: FaUserFriends, tint: "bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300" },
        ].map((c, i) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className={`${CARD} jr-in p-4 flex items-center gap-3`} style={{ animationDelay: `${i * 60}ms` }}>
              <span className={`w-11 h-11 rounded-xl flex items-center justify-center ${c.tint}`}>
                <Icon />
              </span>
              <div className="min-w-0">
                <p className="text-xl font-bold text-slate-900 dark:text-white tabular-nums">{loading && !requests.length ? "–" : c.value}</p>
                <p className="text-xs text-slate-500">{c.label}</p>
                {c.sub && <p className="text-[11px] text-amber-700 dark:text-amber-300 truncate">{c.sub}</p>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className={`${CARD} jr-in mt-4 p-3 flex flex-wrap items-center gap-3`} style={{ animationDelay: "240ms" }}>
        <div className="relative flex-1 min-w-[240px]">
          <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
          <input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search user, user ID, referrer, group or amount" className={`${INPUT} w-full h-10 pl-10 pr-10`} />
          <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
            {query ? (
              <button type="button" onClick={() => setQuery("")} className="p-1.5 text-slate-400 hover:text-slate-700" aria-label="Clear search">
                <FaTimes size={12} />
              </button>
            ) : (
              <kbd className="hidden sm:inline text-xs text-slate-400 border border-slate-200 dark:border-slate-600 rounded px-1.5 py-0.5">/</kbd>
            )}
          </div>
        </div>
        <div className="inline-flex p-1 rounded-xl bg-slate-100 dark:bg-slate-700">
          {[
            { id: "all", label: "All" },
            { id: "youtube", label: "YouTube", icon: FaYoutube, cls: "text-red-600" },
            { id: "facebook", label: "Facebook", icon: FaFacebookSquare, cls: "text-blue-600" },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} type="button" onClick={() => setPlatform(t.id)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all active:scale-95 ${platform === t.id ? "bg-white dark:bg-secondary-dark-bg text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-300"}`}>
                {Icon && <Icon size={12} className={t.cls} />} {t.label}
              </button>
            );
          })}
        </div>
        <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} className={`${INPUT} h-10 px-3 max-w-[220px]`}>
          <option value="all">All groups</option>
          {groupsInList.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={`${INPUT} h-10 px-3`}>
          <option value="oldest">Waiting longest</option>
          <option value="newest">Newest first</option>
          <option value="amount">Highest amount</option>
        </select>
      </div>

      {/* Bulk / result bar */}
      <div className="flex flex-wrap items-center gap-3 mt-4 mb-3">
        <label className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
          <input type="checkbox" checked={allChecked} onChange={() => setChecked(allChecked ? [] : filtered.map((r) => r.join_id))} className="w-4 h-4 accent-cyan-600" />
          Select all
        </label>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          <b>{filtered.length}</b> request{filtered.length === 1 ? "" : "s"}
          {q && (
            <>
              {" "}
              for "<b>{q}</b>"
            </>
          )}
        </p>
        {checked.length > 0 && (
          <div className="jr-fade ml-auto flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              {checked.length} selected ({money(checkedList.reduce((s, r) => s + Number(r.paid_amount || 0), 0))})
            </span>
            <button type="button" onClick={() => setConfirm({ type: "approve", list: checkedList })} className="jr-press inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold">
              <FaCheck size={11} /> Accept all
            </button>
            <button type="button" onClick={() => setConfirm({ type: "reject", list: checkedList })} className="jr-press inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold">
              <FaBan size={11} /> Cancel all
            </button>
            <button type="button" onClick={() => setChecked([])} className="text-sm text-slate-500 hover:underline">
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Tickets */}
      {loading && !requests.length ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[0, 1].map((i) => (
            <div key={i} className={`${CARD} h-56 animate-pulse`} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className={`${CARD} px-5 py-16 text-center`}>
          <FaInbox className="mx-auto text-slate-300" size={36} />
          <p className="mt-3 font-semibold text-slate-700 dark:text-slate-200">{q ? `No request found for "${q}"` : "All caught up"}</p>
          <p className="text-sm text-slate-500">{q ? "Try another search." : "New joining requests will show up here."}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((r, i) => {
            const d = parseAny(r.date_time);
            const old = d && Date.now() - d.getTime() > DAY;
            const sel = checked.includes(r.join_id);
            const gone = leaving[r.join_id];
            return (
              <article
                key={r.join_id}
                className={`jr-ticket ${gone ? "jr-out" : "jr-in"} relative rounded-2xl bg-white dark:bg-secondary-dark-bg border overflow-hidden flex flex-col sm:flex-row ${sel ? "border-cyan-500 ring-2 ring-cyan-500/30" : "border-slate-200 dark:border-slate-700"}`}
                style={{ animationDelay: gone ? "0ms" : `${Math.min(i, 10) * 50}ms` }}
              >
                {gone && (
                  <span className={`jr-stamp absolute top-6 right-6 z-10 px-3 py-1 rounded-lg border-4 text-lg font-black uppercase tracking-widest ${gone === "approved" ? "border-emerald-500 text-emerald-600" : "border-rose-500 text-rose-600"}`}>
                    {gone}
                  </span>
                )}

                {/* Proof */}
                <button
                  type="button"
                  onClick={() => setViewer(r)}
                  className="jr-proof relative sm:w-48 shrink-0 bg-slate-100 dark:bg-slate-800 overflow-hidden"
                  style={{ minHeight: 170 }}
                  aria-label="View payment proof"
                >
                  {hasProof(r.proof_img) ? (
                    <>
                      <img src={r.proof_img} alt="Payment proof" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                      <span className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold text-white bg-gradient-to-t from-black/70 to-transparent">
                        <FaSearchPlus size={10} /> View proof
                      </span>
                    </>
                  ) : (
                    <span className="absolute inset-0 flex flex-col items-center justify-center text-xs text-slate-400 gap-1">
                      <FaImage size={22} /> No proof uploaded
                    </span>
                  )}
                </button>

                {/* perforation */}
                <div className="hidden sm:block w-4 shrink-0 text-slate-200 dark:text-slate-700 jr-perf" />

                {/* Body */}
                <div className="flex-1 p-4 min-w-0 flex flex-col">
                  <div className="flex items-start gap-2">
                    <input type="checkbox" checked={sel} onChange={() => setChecked((c) => (c.includes(r.join_id) ? c.filter((x) => x !== r.join_id) : [...c, r.join_id]))} className="mt-1 w-4 h-4 accent-cyan-600 cursor-pointer" aria-label="Select request" />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <PlatformBadge r={r} />
                        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${old ? "text-amber-700 dark:text-amber-300" : "text-slate-500"}`}>
                          {old && <span className="jr-dot w-1.5 h-1.5 rounded-full bg-amber-500" />}
                          {waiting(d)}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-slate-500">Request #{r.join_id}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-black text-slate-900 dark:text-white tabular-nums">
                        <Hi text={money(r.paid_amount)} q={q} />
                      </p>
                      <p className="text-[11px] text-slate-500">Paid Fees</p>
                    </div>
                  </div>

                  {/* Flow: user -> group, via referrer */}
                  <div className="mt-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 p-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <Initials name={r.user_name} size={34} tone="bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-200" />
                      <div className="min-w-0 flex-1">
                        <button type="button" onClick={() => navigate(`/user-profile/${r.user_id}`)} className="block text-sm font-semibold text-slate-900 dark:text-white truncate hover:underline text-left">
                          <Hi text={nm(r.user_name) || "Unknown user"} q={q} />
                        </button>
                        <p className="text-[11px] text-slate-500 tabular-nums">
                          <Hi text={r.user_id} q={q} />
                        </p>
                      </div>
                      <FaArrowRight className="text-slate-300 shrink-0" size={11} />
                      <div className="min-w-0 flex-1 text-right">
                        <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                          <Hi text={r.grp_name || `Group ${r.group_id}`} q={q} />
                        </p>
                        <p className="text-[11px] text-slate-500 tabular-nums">Group ID {r.group_id}</p>
                      </div>
                    </div>
                    <div className="mt-2 pt-2 border-t border-slate-200/70 dark:border-slate-700 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                      <FaUserFriends className="text-violet-500" size={11} />
                      Member joined by{" "}
                      <button type="button" onClick={() => r.joined_by && navigate(`/user-profile/${r.joined_by}`)} className="font-semibold text-slate-900 dark:text-white hover:underline">
                        <Hi text={nm(r.joined_by_name) || "Direct"} q={q} />
                      </button>
                      {r.joined_by && <span className="text-slate-400 tabular-nums">({r.joined_by})</span>}
                    </div>
                  </div>

                  <p className="mt-2 text-[11px] text-slate-500">
                    Sent {fmtDate(d)}, {fmtTime(d)} <span className="mx-1">•</span> Status: <b className="text-amber-700 dark:text-amber-300">{r.status || "pending"}</b>
                  </p>

                  <div className="mt-auto pt-3 grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setConfirm({ type: "reject", list: [r] })} disabled={!!gone} className="jr-press h-10 rounded-xl border border-rose-200 dark:border-rose-800 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 text-sm font-semibold inline-flex items-center justify-center gap-2">
                      <FaBan size={11} /> Cancel
                    </button>
                    <button type="button" onClick={() => setConfirm({ type: "approve", list: [r] })} disabled={!!gone} className="jr-press h-10 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 shadow-sm">
                      <FaCheck size={11} /> Accept
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* ================= Proof viewer ================= */}
      {viewer && (
        <div className="fixed inset-0 flex items-center justify-center p-3 sm:p-6" style={{ zIndex: 1100 }}>
          <div className="jr-fade absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={() => setViewer(null)} />
          <div className="jr-zoom relative w-full max-w-5xl grid grid-cols-1 lg:grid-cols-3 rounded-2xl overflow-hidden bg-slate-900 text-white shadow-2xl max-h-[92vh]">
            <div className="lg:col-span-2 relative bg-black flex flex-col min-h-[320px]">
              <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-white/10">
                <span className="text-sm font-medium inline-flex items-center gap-2">
                  <FaReceipt /> Payment proof
                </span>
                <div className="flex gap-1">
                  <button type="button" onClick={() => setZoom((z) => Math.max(1, z - 0.5))} className="jr-press w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center" aria-label="Zoom out"><FaSearchMinus size={12} /></button>
                  <button type="button" onClick={() => setZoom((z) => Math.min(4, z + 0.5))} className="jr-press w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center" aria-label="Zoom in"><FaSearchPlus size={12} /></button>
                  <button type="button" onClick={() => setRotate((x) => (x + 90) % 360)} className="jr-press w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center" aria-label="Rotate"><FaRedo size={11} /></button>
                  {hasProof(viewer.proof_img) && (
                    <a href={viewer.proof_img} target="_blank" rel="noreferrer" className="jr-press w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center" aria-label="Open original"><FaExternalLinkAlt size={11} /></a>
                  )}
                </div>
              </div>
              <div className="flex-1 overflow-auto flex items-center justify-center p-4">
                {hasProof(viewer.proof_img) ? (
                  <img
                    src={viewer.proof_img}
                    alt="Payment proof"
                    onClick={() => setZoom((z) => (z === 1 ? 2 : 1))}
                    className="max-w-full max-h-[75vh] object-contain cursor-zoom-in transition-transform duration-300"
                    style={{ transform: `scale(${zoom}) rotate(${rotate}deg)` }}
                  />
                ) : (
                  <p className="text-white/60 text-sm">No proof uploaded with this request.</p>
                )}
              </div>
            </div>

            <div className="flex flex-col min-h-0">
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                <span className="text-xs text-white/60">Request #{viewer.join_id}</span>
                <button type="button" onClick={() => setViewer(null)} className="jr-press w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center" aria-label="Close">
                  <FaTimes size={12} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div className="rounded-xl bg-emerald-500/15 border border-emerald-400/30 p-4 text-center">
                  <p className="text-xs text-emerald-200">Amount the user says they paid</p>
                  <p className="text-4xl font-black tabular-nums">{money(viewer.paid_amount)}</p>
                  <p className="text-[11px] text-white/60 mt-1">Match this with the screenshot and your bank or UPI app</p>
                </div>
                {[
                  { label: "User", name: nm(viewer.user_name) || "Unknown user", id: viewer.user_id },
                  { label: "Group", name: viewer.grp_name || `Group ${viewer.group_id}`, id: viewer.group_id },
                  { label: "Member joined by", name: nm(viewer.joined_by_name) || "Direct", id: viewer.joined_by },
                ].map((row) => (
                  <div key={row.label} className="rounded-xl bg-white/[.06] p-3">
                    <p className="text-[11px] text-white/50">{row.label}</p>
                    <p className="font-semibold truncate">{row.name}</p>
                    {row.id && (
                      <button type="button" onClick={() => copyText(row.id, "ID copied")} className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1 tabular-nums">
                        {row.id} <FaCopy size={9} />
                      </button>
                    )}
                  </div>
                ))}
                <p className="text-xs text-white/60">
                  Sent {fmtDate(parseAny(viewer.date_time))}, {fmtTime(parseAny(viewer.date_time))}
                </p>
                <p className="text-[11px] text-white/40">Keyboard: A to accept, R to cancel, Esc to close</p>
              </div>
              <div className="grid grid-cols-2 gap-2 p-4 border-t border-white/10">
                <button type="button" onClick={() => setConfirm({ type: "reject", list: [viewer] })} className="jr-press h-11 rounded-xl bg-white/10 hover:bg-rose-600 text-sm font-semibold inline-flex items-center justify-center gap-2">
                  <FaBan size={11} /> Cancel
                </button>
                <button type="button" onClick={() => setConfirm({ type: "approve", list: [viewer] })} className="jr-press h-11 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-sm font-semibold inline-flex items-center justify-center gap-2">
                  <FaCheck size={11} /> Accept
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= Confirm ================= */}
      {confirm && confirm.list.length > 0 && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1200 }}>
          <div className="jr-fade absolute inset-0 bg-slate-900/60" onClick={() => !working && setConfirm(null)} />
          <div className="jr-zoom relative w-full max-w-md rounded-2xl bg-white dark:bg-secondary-dark-bg shadow-2xl p-5">
            <span className={`w-12 h-12 rounded-2xl flex items-center justify-center ${confirm.type === "approve" ? "bg-emerald-100 text-emerald-600" : "bg-rose-100 text-rose-600"}`}>
              {confirm.type === "approve" ? <FaCheck size={18} /> : <FaExclamationTriangle size={18} />}
            </span>
            <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">
              {confirm.type === "approve"
                ? confirm.list.length === 1
                  ? `Accept ${nm(confirm.list[0].user_name) || "this user"}?`
                  : `Accept ${confirm.list.length} requests?`
                : confirm.list.length === 1
                ? `Cancel ${nm(confirm.list[0].user_name) || "this user"}'s request?`
                : `Cancel ${confirm.list.length} requests?`}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {confirm.type === "approve"
                ? "The user is added to the group (and to the master group), the referrer gets their commission, and the user is notified."
                : "The request is marked as rejected and the user gets a notification with the reason."}
            </p>

            <div className="mt-4 max-h-36 overflow-y-auto rounded-xl bg-slate-50 dark:bg-slate-800/60 divide-y divide-slate-100 dark:divide-slate-700">
              {confirm.list.map((r) => (
                <div key={r.join_id} className="flex items-center gap-3 p-2.5">
                  <Initials name={r.user_name} size={28} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{nm(r.user_name) || r.user_id}</p>
                    <p className="text-xs text-slate-500 truncate">{r.grp_name}</p>
                  </div>
                  <span className="text-sm font-bold tabular-nums text-slate-900 dark:text-white">{money(r.paid_amount)}</span>
                </div>
              ))}
            </div>

            {confirm.type === "reject" && (
              <div className="mt-4">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">Reason (sent to the user)</p>
                <div className="flex flex-wrap gap-2">
                  {[...REASONS, "__custom"].map((rs) => (
                    <button
                      key={rs}
                      type="button"
                      onClick={() => setReason(rs)}
                      className={`jr-press h-8 px-3 rounded-full text-xs font-semibold border ${
                        reason === rs ? "bg-rose-600 text-white border-rose-600" : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600"
                      }`}
                    >
                      {rs === "__custom" ? "Write my own" : rs}
                    </button>
                  ))}
                </div>
                {reason === "__custom" && (
                  <textarea rows={2} value={customReason} onChange={(e) => setCustomReason(e.target.value)} autoFocus placeholder="Write the reason" className={`${INPUT} w-full mt-2 px-3 py-2`} />
                )}
              </div>
            )}

            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setConfirm(null)} disabled={working} className="jr-press flex-1 h-11 rounded-xl border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200">
                Go back
              </button>
              <button
                type="button"
                onClick={runConfirm}
                disabled={working || (confirm.type === "reject" && reason === "__custom" && !customReason.trim())}
                className={`jr-press flex-1 h-11 rounded-xl text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 ${confirm.type === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}
              >
                {working ? <FaSyncAlt className="animate-spin" size={12} /> : confirm.type === "approve" ? <FaCheck size={12} /> : <FaBan size={12} />}
                {working ? "Please wait..." : confirm.type === "approve" ? "Yes, accept" : "Yes, cancel request"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupJoinRequest;